/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";
import U2000SaveCancelDialog from "../../u2000Core/components/dialog/U2000SaveCancelDialog";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { U2000ExtractFirstApiError } from "../../u2000Core/axios/U2000Axios";
import type { U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import u2000FormState, { U2000FormStateEnumAction, U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS, U3125ROLES } from "../U3125";
import U3125FactureBody from "./form/u3125Facture/U3125FactureBody";
import U3125InvoiceLinesTabBody from "./form/u3125Facture/u3125FactureInvoiceLinesTab/U3125InvoiceLinesTabBody";
import U3125WipLinesTabBody from "./form/u3125Facture/u3125FactureWipLinesTab/U3125WipLinesTabBody";
import U3125PaymentsTabBody from "./form/u3125Facture/u3125FacturePaymentsTab/U3125PaymentsTabBody";
import U3125CreditApplicationsTabBody from "./form/u3125Facture/u3125FactureCreditApplicationsTab/U3125CreditApplicationsTabBody";
import U3125SourceUsageTabBody from "./form/u3125Facture/u3125FactureSourceUsageTab/U3125SourceUsageTabBody";
import { U3125FactureFormToolBar } from "./form/u3125Facture/U3125FactureFormToolBar";
import U3125FactureFormDeleteDlg from "./common/U3125FactureFormDeleteDlg";
import U3125FactureVoidDlg from "./common/U3125FactureVoidDlg";
import { useU3125Context } from "../U3125Context";
import { useU3125InitCreateInvoiceDialog } from "../U3125InitCreateInvoiceDialogContext";
import type { U3125FormNavigationState } from "../U3125InitCreateInvoiceDialogContext";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  doGetAuditsData,
  emptyInvoiceForm,
  luHandleAppiError,
  mapInitPreviewToFormRow,
  mapInvoiceToFormRow,
  parseWipTransactionsFromResult,
  buildInvoiceVoidBody,
  buildInvoiceSetStatusBody,
  u3125BuildFullBalancePayment,
  u3125FormatInvoiceDeleteLabel,
  u3125IsInvoiceFullyPaidByPayments,
  u3125PaymentComponentsTotal,
  u3125PaymentRemainingBalanceForInvoice,
  u3125CanModifyInvoice,
  u3125ShowReleaseAfterCreateCheckbox,
  u3125ShowInvoiceWipLinesTab,
  u3125FilterTaxRatesForInvoiceSelection,
  u3125InvoiceAmountsExceedRemainingErrors,
  u3125ShouldApplyInvoiceRemainingCapValidation,
  u3125InvoiceRequiresTaxRateCode,
  u3125IsWriteOffDocument,
  u3125ShowInvoiceLinesTab,
  u3125ResolveTabsWithValidationErrors,
  u3125ValidateCreditApplications,
  u3125TaxRateCodeAllowed,
  type U3125Invoice,
  type U3125InitCreateNewInvoice,
  type U3125InvoiceFormRow,
  type U3125InvoiceSearchRow,
  type U3125InvoiceStatusInitItem,
  type U3125InvoiceTypeInitItem,
  type U3125PaymentTypeInitItem,
  type U3125TaxRateInitItem,
} from "../U3125Service";
import { useU3125FindProjectHandlers } from "../U3125UseFindProjectHandlers";
import { useU3125FormDeleteHandlers, useU3125GetHandlers, useU3125PaymentApiHandlers, useU3125SaveHandlers, useU3125SearchHandlers, useU3125SetInvoiceStatusHandlers, useU3125VoidInvoiceHandlers } from "../U3125UseApiHandlers";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import type { U3134WorkInProgressSearchRow } from "../../u3134WorkInProgress/U3134Service";

export interface U3125FactureFormExtendedHandlers extends U2000FormStateHandlersReturn<U3125InvoiceFormRow> {
  addNewInvoice: () => void;
  modifyInvoice: () => void;
  deleteInvoice: () => void;
}

const U3125FactureForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3125Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [activeTab, setActiveTab] = useState<string>("tabWipLines");
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const listTitle = t("title", { ns: NS });
  const toast = useToast();

  const canRelease = hasRole(U3125ROLES.EDIT);
  const canReleaseOnCreate = hasRole(U3125ROLES.ADD);
  const canViewTecLines = hasRole(U3125ROLES.VIEWTECLINES);
  const [releaseAfterCreate, setReleaseAfterCreate] = useState(false);
  const [confirmReleaseOpen, setConfirmReleaseOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [closePending, setClosePending] = useState(false);
  const pendingSaveRef = useRef<{ editMode: U2000FormStateEnumEditMode } | null>(null);

  const { openAddDialog } = useU3125InitCreateInvoiceDialog();
  const findProjectHandlers = useU3125FindProjectHandlers();
  const [wipCandidates, setWipCandidates] = useState<U3134WorkInProgressSearchRow[]>([]);
  const [wipLoading] = useState(false);

  const personalizeInit = useCallback(() => {
    const p = luStateHandlers.getPartialPersonnalization([
      "invoiceTypes",
      "invoiceStatuses",
      "taxRates",
      "paymentTypes",
      "enableInvoicePdfAndLines",
      "allowInvoiceAboveBudget",
    ]) as {
      invoiceTypes?: U3125InvoiceTypeInitItem[];
      invoiceStatuses?: U3125InvoiceStatusInitItem[];
      taxRates?: U3125TaxRateInitItem[];
      paymentTypes?: U3125PaymentTypeInitItem[];
      enableInvoicePdfAndLines?: boolean;
      allowInvoiceAboveBudget?: boolean;
    };
    return {
      invoiceTypes: p?.invoiceTypes ?? [],
      invoiceStatuses: p?.invoiceStatuses ?? [],
      taxRates: p?.taxRates ?? [],
      paymentTypes: p?.paymentTypes ?? [],
      enableInvoicePdfAndLines: Boolean(p?.enableInvoicePdfAndLines),
      allowInvoiceAboveBudget: Boolean(p?.allowInvoiceAboveBudget),
    };
  }, [luStateHandlers]);

  const buildInitialEmpty = useCallback((): U3125InvoiceFormRow => {
    const empty = emptyInvoiceForm();
    empty.status = "OPEN";
    const { invoiceTypes, taxRates } = personalizeInit();
    const firstActive = invoiceTypes.find((it) => it.active);
    if (firstActive?.code) empty.invoiceTypeCode = firstActive.code;
    const selectable = u3125FilterTaxRatesForInvoiceSelection(taxRates, {
      customerTaxable: empty.customerTaxable,
      invoiceCountryId: empty.country?.id,
      invoiceProvinceId: empty.province?.id,
    });
    if (selectable.length === 1) {
      const onlyRate = selectable[0];
      empty.taxRateCode = (onlyRate.code?.trim() || onlyRate.id) ?? "";
    }
    return empty;
  }, [personalizeInit]);

  const validateForm = (row: U3125InvoiceFormRow) => {
    const result: Record<string, string> = {};
    if (!row.project?.id?.trim()) {
      result.project = t("errProjectRequired", { ns: NS });
    }
    if (!row.invoiceTypeCode?.trim()) {
      result.invoiceTypeCode = t("errInvoiceTypeCodeRequired", { ns: NS });
    }
    const { invoiceTypes: typesForValidation } = personalizeInit();
    if (u3125InvoiceRequiresTaxRateCode(row, typesForValidation)) {
      if (!row.taxRateCode?.trim()) {
        result.taxRateCode = t("errTaxRateCodeRequired", { ns: NS });
      } else {
        const { taxRates: trInit } = personalizeInit();
        const selectable = u3125FilterTaxRatesForInvoiceSelection(trInit, {
          customerTaxable: row.customerTaxable,
          invoiceCountryId: row.country?.id,
          invoiceProvinceId: row.province?.id,
        });
        if (!u3125TaxRateCodeAllowed(row.taxRateCode, selectable)) {
          result.taxRateCode = t("errTaxRateCodeInvalid", { ns: NS });
        }
      }
    }
    if (!row.invoiceDate?.trim()) {
      result.invoiceDate = t("errInvoiceDateRequired", { ns: NS });
    }
    if (u3125IsWriteOffDocument(row.documentNature)) {
      if ((row.workInProgressLineIds?.length ?? 0) === 0) {
        result.workInProgressLineIds = t("errWriteOffNoWipSelected", { ns: NS });
      }
    } else {
      const { allowInvoiceAboveBudget } = personalizeInit();
      if (u3125ShouldApplyInvoiceRemainingCapValidation(allowInvoiceAboveBudget, row.documentNature)) {
        Object.assign(result, u3125InvoiceAmountsExceedRemainingErrors(row, t("errInvoiceAmountsExceedRemaining", { ns: NS })));
      }
    }
    if (row.documentNature === "STANDARD") {
      Object.assign(result, u3125ValidateCreditApplications(row, t, NS));
    }
    return result;
  };

  const u3125BL: U2000FormSBusinessHandlers<U3125InvoiceFormRow> = {
    newRecord: () => buildInitialEmpty(),
    validateForm,
    saveForme: async (row: U3125InvoiceFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3125FactureExtended.setErrors(errors);
        return;
      }
      const showRelease = u3125ShowReleaseAfterCreateCheckbox(editMode, row.status, formState.state);
      const canOfficializeOnSave = editMode === U2000FormStateEnumEditMode.ADD ? canReleaseOnCreate : canRelease;
      if (showRelease && releaseAfterCreate && !canOfficializeOnSave) {
        toast.warning(t("errWizardReleaseRequiresEdit", { ns: NS }), t("lblWarningsTitle", { ns: NS }));
        return;
      }
      if (showRelease && releaseAfterCreate) {
        pendingSaveRef.current = { editMode };
        setConfirmReleaseOpen(true);
        return;
      }
      const saveOptions = { releaseAfterCreate: Boolean(showRelease && releaseAfterCreate) };
      (handleSave.saveData as any)(row, editMode, saveOptions);
    },
    cancelForm: () => {
      const old = u3125FactureExtended.getOldData() as U3125InvoiceFormRow | null;
      if (old) {
        u3125FactureExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3125") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => {
      navigate("/U3125");
    },
  };

  const u3125FormHandlers = useU2000FormStateHandlers<U3125InvoiceFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3125BL,
  });

  const u3125FactureExtended: U3125FactureFormExtendedHandlers = {
    ...u3125FormHandlers,
    addNewInvoice: () => {
      openAddDialog();
    },
    modifyInvoice: () => {
      const d = u3125FormHandlers.getData();
      if (!u3125CanModifyInvoice(d?.status)) {
        toast.warning(t("msgModifyBlockedNotOpen", { ns: NS }), t("lblWarningsTitle", { ns: NS }));
        return;
      }
      setReleaseAfterCreate(false);
      u3125FactureExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteInvoice: () => {
      handleDelete.handleDelete();
    },
  };

  const refreshWipAfterLoad = useCallback((invoicePayload: unknown, projectId?: string) => {
    if (!projectId?.trim()) {
      setWipCandidates([]);
      return;
    }
    setWipCandidates(parseWipTransactionsFromResult(invoicePayload));
  }, []);

  const handleGet = useU3125GetHandlers<U3125Invoice>({
    onSuccess: (inv) => {
      const normalized = mapInvoiceToFormRow(inv);
      gridStateHandlers.setDataById(normalized.id, normalized as unknown as U3125InvoiceSearchRow);
      u3125FactureExtended.setData(normalized);
      const invoiceId = String(normalized.id);
      if (!location.pathname.endsWith(invoiceId)) {
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, invoiceId);
        navigate(newPath, { replace: true });
      }
      const p = normalized.project;
      const crumbLabel = p?.projetNumber ? `${p.projetNumber} — ${p.projetName ?? ""}` : normalized.id;
      setLogicalUnitBreadcrumb([{ href: "/U3125", label: listTitle }, { label: crumbLabel }]);
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY && u3125CanModifyInvoice(normalized.status)) {
        setReleaseAfterCreate(false);
        u3125FactureExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
      refreshWipAfterLoad(inv, normalized.project?.id);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSearch = useU3125SearchHandlers<U3125InvoiceSearchRow>({
    onSuccess: (searchResult: U2000SearchResult<U3125InvoiceSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      if (searchResult.data[idx]?.id) {
        handleGet.getData(searchResult.data[idx].id);
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU3125SaveHandlers({
    onWarnings: (warnings) => {
      warnings.forEach((w) => toast.warning(w, t("lblWarningsTitle", { ns: NS })));
    },
    onSuccess: (normalized: U3125InvoiceFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), normalized as unknown as U3125InvoiceSearchRow]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(normalized.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(normalized.id, normalized as unknown as U3125InvoiceSearchRow);
      }
      u3125FactureExtended.afterSaveData(normalized);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3125") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
      if (normalized.id?.trim()) {
        handleGet.getData(normalized.id);
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorCode = U2000ExtractFirstApiError(error)?.errorCode;
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3125FactureExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3125FactureExtended.setErrors(errorResult.fieldErrors);
      }
      if (errorCode === "B0002") {
        const invoiceId = u3125FactureExtended.getData()?.id?.trim();
        if (invoiceId) {
          void handleGet.getData(invoiceId);
        }
      }
    },
  });

  const voidInvoiceHandlers = useU3125VoidInvoiceHandlers({
    onSuccess: (row) => {
      gridStateHandlers.setDataById(row.id, row as unknown as U3125InvoiceSearchRow);
      u3125FactureExtended.afterSaveData(row);
      toast.success(t("msgVoidSuccess", { ns: NS }));
      void handleGet.getData(row.id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleVoidDialogClose = useCallback(
    async (result: "yes" | "no") => {
      setVoidDialogOpen(false);
      if (result !== "yes") return;
      const current = u3125FactureExtended.getData();
      if (!current?.id?.trim()) return;
      await voidInvoiceHandlers.voidInvoice(current.id, buildInvoiceVoidBody(current));
    },
    [u3125FactureExtended, voidInvoiceHandlers],
  );

  const handleDelete = useU3125FormDeleteHandlers({
    getSelectedRows: () => [u3125FactureExtended.getData()],
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((del) => {
        toast.success(t("msgDeleteSuccess", { ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3125", { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const { invoiceTypes, invoiceStatuses, taxRates, paymentTypes } = personalizeInit();

  const handlePaymentApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const paymentApi = useU3125PaymentApiHandlers({
    parentFormHandlers: u3125FactureExtended,
    onError: handlePaymentApiError,
  });

  const setInvoiceStatusHandlers = useU3125SetInvoiceStatusHandlers({
    onSuccess: (row) => {
      u3125FactureExtended.afterSaveData(row);
      gridStateHandlers.setDataById(row.id, row as unknown as U3125InvoiceSearchRow);
    },
    onError: handlePaymentApiError,
  });

  const collectFullBalanceAndClose = useCallback(async () => {
    const invoice = u3125FactureExtended.getData();
    if (!invoice?.id?.trim() || invoice.status !== "RELEASE") {
      return;
    }

    setClosePending(true);
    try {
      const remaining = u3125PaymentRemainingBalanceForInvoice(
        invoice,
        invoice.payments ?? [],
        taxRates,
      );
      const amountDue = u3125PaymentComponentsTotal(remaining);

      if (amountDue <= 0.01) {
        if (u3125IsInvoiceFullyPaidByPayments(invoice, taxRates, invoiceTypes)) {
          const updated = await setInvoiceStatusHandlers.applyStatus(
            invoice.id,
            buildInvoiceSetStatusBody(invoice, "CLOSE"),
          );
          if (updated) {
            toast.success(t("msgInvoiceClosedAfterFullPayment", { ns: NS }));
          }
        } else {
          toast.error(t("msgInvoiceCannotCloseInsufficientPayments", { ns: NS }));
          await handleGet.getData(invoice.id);
        }
        return;
      }

      const payment = u3125BuildFullBalancePayment(invoice, taxRates);
      if (!payment) {
        toast.error(t("msgInvoiceCannotCloseInsufficientPayments", { ns: NS }));
        await handleGet.getData(invoice.id);
        return;
      }

      const ok = await paymentApi.createPayment(payment);
      if (ok) {
        const updated = u3125FactureExtended.getData();
        if (updated?.id) {
          gridStateHandlers.setDataById(updated.id, updated as unknown as U3125InvoiceSearchRow);
        }
        toast.success(
          updated?.status === "CLOSE"
            ? t("msgInvoiceClosedAfterFullPayment", { ns: NS })
            : t("msgInvoiceStatusUpdated", { ns: NS }),
        );
      }
    } finally {
      setClosePending(false);
    }
  }, [
    u3125FactureExtended,
    taxRates,
    invoiceTypes,
    setInvoiceStatusHandlers,
    paymentApi,
    handleGet,
    toast,
    t,
    gridStateHandlers,
  ]);

  const prevProjectIdForWipRef = useRef<string | null>(null);
  const skipNextWipProjectResetRef = useRef(false);

  const navInitPreview = (location.state as U3125FormNavigationState | null)?.initPreview;
  const initPreviewRef = useRef<U3125InitCreateNewInvoice | undefined>(undefined);
  if (navInitPreview) {
    initPreviewRef.current = navInitPreview;
  }
  const effectiveInitPreview = navInitPreview ?? initPreviewRef.current;
  const initPreviewKey = effectiveInitPreview
    ? `${effectiveInitPreview.documentNature ?? "STANDARD"}:${effectiveInitPreview.project?.id ?? ""}:${effectiveInitPreview.invoiceTypeCode ?? ""}:${effectiveInitPreview.invoiceDate ?? ""}:${effectiveInitPreview.workInProgressLineIds?.length ?? 0}:${effectiveInitPreview.availableCreditAlert?.showAlert ?? false}:${effectiveInitPreview.availableCreditAlert?.customer?.creditRemaining ?? 0}:${effectiveInitPreview.availableCreditAlert?.customer?.advanceRemaining ?? 0}`
    : null;

  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      setReleaseAfterCreate(canReleaseOnCreate);
      if (effectiveInitPreview) {
        const { taxRates } = personalizeInit();
        const mapped = mapInitPreviewToFormRow(effectiveInitPreview, taxRates);
        const projectId = mapped.row.project?.id?.trim() ?? null;
        skipNextWipProjectResetRef.current = true;
        prevProjectIdForWipRef.current = projectId;
        formStateDispatch({
          type: U2000FormStateEnumAction.SETEDITMODE,
          data: { mode: U2000FormStateEnumEditMode.ADD },
        });
        u3125FactureExtended.setData(mapped.row);
        setWipCandidates(canViewTecLines ? mapped.wipCandidates : []);
        setActiveTab(
          u3125ShowInvoiceWipLinesTab(mapped.row.documentNature ?? "STANDARD", canViewTecLines)
            ? "tabWipLines"
            : "tabInvoiceLines",
        );
        const p = mapped.row.project;
        const crumb = p?.projetNumber ? `${p.projetNumber} — ${p.projetName ?? ""}` : t("lblNewInvoice", { ns: NS });
        setLogicalUnitBreadcrumb([{ href: "/U3125", label: listTitle }, { label: crumb }]);
      } else {
        prevProjectIdForWipRef.current = null;
        u3125FactureExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
        u3125FactureExtended.setData(buildInitialEmpty());
        setLogicalUnitBreadcrumb([{ href: "/U3125", label: listTitle }, { label: t("lblNewInvoice", { ns: NS }) }]);
        setWipCandidates([]);
      }
    } else {
      handleGet.getData(id);
    }
  }, [id, initPreviewKey, canRelease, canViewTecLines]);

  const projId = u3125FormHandlers.getData()?.project?.id;
  const invoiceIdForWip = u3125FormHandlers.getData()?.id?.trim() || undefined;
  useEffect(() => {
    if (invoiceIdForWip?.trim()) {
      prevProjectIdForWipRef.current = projId?.trim() ?? null;
      return;
    }
    const pid = projId?.trim() ?? "";
    if (skipNextWipProjectResetRef.current) {
      skipNextWipProjectResetRef.current = false;
      prevProjectIdForWipRef.current = pid || null;
      return;
    }
    const prev = prevProjectIdForWipRef.current;
    if (canViewTecLines && prev && pid && prev !== pid) {
      setWipCandidates([]);
      const d = u3125FactureExtended.getData();
      if (d) {
        u3125FactureExtended.setData({
          ...d,
          workInProgressLineIds: [],
          amountTec: 0,
          amountInvoiceTaxable: 0,
          amountInvoiceNonTaxable: 0,
          taxAmount1: null,
          taxAmount2: null,
        });
      }
    }
    prevProjectIdForWipRef.current = pid || null;
  }, [projId, invoiceIdForWip, canViewTecLines, u3125FactureExtended]);

  const formData = u3125FactureExtended.getData();
  const formDocumentNature = formData?.documentNature ?? "STANDARD";
  const isWriteOff = u3125IsWriteOffDocument(formDocumentNature);
  const showWipTab = u3125ShowInvoiceWipLinesTab(formDocumentNature, canViewTecLines);
  const showPaymentsTab = formDocumentNature === "STANDARD" || formDocumentNature === "ADVANCE";
  const enableInvoicePdfAndLines = useMemo(() => personalizeInit().enableInvoicePdfAndLines, [personalizeInit]);
  const showInvoiceLinesTab = u3125ShowInvoiceLinesTab(enableInvoicePdfAndLines, isWriteOff);
  const defaultTabId = showWipTab ? "tabWipLines" : showInvoiceLinesTab ? "tabInvoiceLines" : "tabAudits";

  useEffect(() => {
    if (!showWipTab && activeTab === "tabWipLines") {
      setActiveTab(showInvoiceLinesTab ? "tabInvoiceLines" : defaultTabId);
    }
  }, [showWipTab, showInvoiceLinesTab, activeTab, defaultTabId]);

  useEffect(() => {
    if (!showInvoiceLinesTab && activeTab === "tabInvoiceLines") {
      setActiveTab(defaultTabId);
    }
  }, [showInvoiceLinesTab, activeTab, defaultTabId]);

  useEffect(() => {
    if (!showPaymentsTab && activeTab === "tabPayments") {
      setActiveTab(defaultTabId);
    }
  }, [showPaymentsTab, activeTab, defaultTabId]);

  const tabsWithErrors = useMemo(
    () => u3125ResolveTabsWithValidationErrors((formState.errors ?? {}) as Record<string, string>),
    [formState.errors],
  );

  const formEditMode = u3125FactureExtended.getFormEditMode();
  const showReleaseAfterCreate = u3125ShowReleaseAfterCreateCheckbox(formEditMode, formData?.status, formState.state);

  const runPendingSave = useCallback(() => {
    const pending = pendingSaveRef.current;
    if (!pending) return;
    pendingSaveRef.current = null;
    const row = u3125FactureExtended.getData();
    if (!row) return;
    const showRelease = u3125ShowReleaseAfterCreateCheckbox(pending.editMode, row.status, formState.state);
    const saveOptions = { releaseAfterCreate: Boolean(showRelease && releaseAfterCreate) };
    (handleSave.saveData as any)(row, pending.editMode, saveOptions);
  }, [handleSave, releaseAfterCreate, formState.state, u3125FactureExtended]);

  const releaseAfterCreateDisabled = formEditMode === U2000FormStateEnumEditMode.ADD ? !canReleaseOnCreate : !canRelease;

  if (formData === null) {
    return null;
  }

  const formDataId = formData.id?.trim() ?? "";

  const detailTabs: { id: string; label: string; content: React.ReactNode }[] = [];
  if (showWipTab) {
    detailTabs.push({
      id: "tabWipLines",
      label: t("sectionWipLines", { ns: NS }),
      content: (
        <U3125WipLinesTabBody
          formStateHandlers={u3125FactureExtended as never}
          wipCandidates={wipCandidates}
          wipLoading={wipLoading}
          taxRates={taxRates}
          onReplaceWipCandidates={(rows) => setWipCandidates(rows)}
        />
      ),
    });
  }
  if (showPaymentsTab) {
    detailTabs.push({
      id: "tabPayments",
      label: t("sectionPayments", { ns: NS }),
      content: (
        <U3125PaymentsTabBody
          parentFormHandlers={u3125FactureExtended as never}
          paymentTypes={paymentTypes}
          taxRates={taxRates}
        />
      ),
    });
  }
  if (formDocumentNature === "STANDARD") {
    detailTabs.push({
      id: "tabCreditApplications",
      label: t("sectionCreditApplications", { ns: NS }),
      content: <U3125CreditApplicationsTabBody parentFormHandlers={u3125FactureExtended as never} />,
    });
  }
  if (formDocumentNature === "CREDIT" || formDocumentNature === "ADVANCE") {
    detailTabs.push({
      id: "tabSourceUsage",
      label: t("tabSourceUsageInvoices", { ns: NS }),
      content: (
        <U3125SourceUsageTabBody parentFormHandlers={u3125FactureExtended as never} />
      ),
    });
  }
  if (showInvoiceLinesTab) {
    detailTabs.push({
      id: "tabInvoiceLines",
      label: t("sectionInvoiceLines", { ns: NS }),
      content: <U3125InvoiceLinesTabBody parentFormHandlers={u3125FactureExtended as never} />,
    });
  }
  if (hasRole(U3125ROLES.AUDITS) && formDataId) {
    detailTabs.push({
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={formDataId} doGetAuditsData={doGetAuditsData} toastName="U3125FactureAudit" />,
    });
  }

  return (
    <>
      <U3125FactureFormDeleteDlg handleDelete={handleDelete as never} t={t} NS={NS} />
      <U3125FactureVoidDlg
        isOpen={voidDialogOpen}
        onClose={(result) => void handleVoidDialogClose(result)}
        invoiceLabel={u3125FormatInvoiceDeleteLabel(u3125FactureExtended.getData() ?? {})}
        documentNature={u3125FactureExtended.getData()?.documentNature ?? "STANDARD"}
        t={t}
        NS={NS}
      />
      <U2000SaveCancelDialog
        isOpen={confirmReleaseOpen}
        title={t("wizardReleaseConfirmTitle", { ns: NS })}
        onClose={(action) => {
          setConfirmReleaseOpen(false);
          if (action === "save") {
            runPendingSave();
          } else {
            pendingSaveRef.current = null;
          }
        }}
        width="medium"
      >
        <p className="text-sm text-gray-700">{t("wizardReleaseConfirmMessage", { ns: NS })}</p>
      </U2000SaveCancelDialog>
      <U2000DefaultForm
        actionsBtns={
          <U3125FactureFormToolBar
            formStateHandlers={u3125FactureExtended as never}
            onStatusApplied={(row) => {
              gridStateHandlers.setDataById(row.id, row as unknown as U3125InvoiceSearchRow);
              handleGet.getData(row.id);
            }}
            onVoidClick={() => setVoidDialogOpen(true)}
            onCollectFullBalanceAndClose={collectFullBalanceAndClose}
            closePending={closePending}
          />
        }
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3125FactureExtended as never}
      >
        <U3125FactureBody
          formStateHandlers={u3125FactureExtended as never}
          invoiceRow={formData}
          t={t}
          invoiceTypes={invoiceTypes}
          invoiceStatuses={invoiceStatuses}
          taxRates={taxRates}
          findProjectHandlers={findProjectHandlers}
          i18nLanguage={i18n.language}
          routeParamId={id}
          projectLocked={id === "new" && !!(location.state as U3125FormNavigationState | null)?.initPreview}
          showReleaseAfterCreate={showReleaseAfterCreate}
          releaseAfterCreate={releaseAfterCreate}
          onReleaseAfterCreateChange={setReleaseAfterCreate}
          releaseAfterCreateDisabled={releaseAfterCreateDisabled}
          canRelease={canRelease}
        />
        <U2000Tab
          className="mt-4"
          tabs={detailTabs}
          defaultTab={defaultTabId}
          activeTab={activeTab}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          tabsWithErrors={tabsWithErrors}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3125FactureForm;
